import { z } from 'zod';
import { isOnline } from './device.format';

// --- Enums ---

export type DeviceType = 'UNASSIGNED' | 'BLE_POSITIONING' | 'GATEWAY' | 'E_LABEL' | 'DOOR_SENSOR';
export type DeviceStatus = 'unassigned' | 'active' | 'maintenance' | 'decommissioned';
export enum Connectivity {
  ONLINE = 'ONLINE',
  OFFLINE = 'OFFLINE',
}

// --- Metadata (read-only, written by external pipelines) ---

export interface DeviceMetadata {
  mac: string;
  type: string;
  uuid: string | null;
  major: number | null;
  minor: number | null;
  ver: string | null;
  screen: string | null;
  product: string | null;
  battery: number | null;
  rssi_at_xm: number | null;
  gateway_mac: string | null;
  state_open: boolean | null;
  uninstalled: boolean | null;
  updated_at: string;
}

export const deviceTypeSchema = z.enum([
  'UNASSIGNED',
  'BLE_POSITIONING',
  'GATEWAY',
  'E_LABEL',
  'DOOR_SENSOR',
]);
export const deviceStatusSchema = z.enum(['unassigned', 'active', 'maintenance', 'decommissioned']);

// --- Device model ---

export interface Device {
  name: string | null;
  device_type: DeviceType;
  mac_address: string;
  status: DeviceStatus;
  connectivity: Connectivity;
  last_seen_at: string | null;
  organization_slug: string | null;
  created_at: string;
  updated_at: string;
  meta: DeviceMetadata | null;
}

const deviceMetadataSchema = z.object({
  mac: z.string(),
  type: z.string(),
  uuid: z.string().nullable(),
  major: z.number().nullable(),
  minor: z.number().nullable(),
  ver: z.string().nullable(),
  screen: z.string().nullable(),
  product: z.string().nullable(),
  battery: z.number().nullable(),
  rssi_at_xm: z.number().nullable(),
  gateway_mac: z.string().nullable(),
  state_open: z.boolean().nullable(),
  uninstalled: z.boolean().nullable(),
  updated_at: z.string(),
});

const normalizeDevice = z
  .object({
    name: z.string().nullable(),
    device_type: deviceTypeSchema,
    mac_address: z.string(),
    status: deviceStatusSchema,
    last_seen: z.string().nullable().optional(),
    last_seen_at: z.string().nullable().optional(),
    organization_slug: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
    meta: deviceMetadataSchema.nullable().optional(),
  })
  .transform((d) => ({
    name: d.name,
    device_type: d.device_type,
    mac_address: d.mac_address,
    status: d.status,
    connectivity: isOnline(d.last_seen_at ?? d.last_seen ?? null)
      ? Connectivity.ONLINE
      : Connectivity.OFFLINE,
    last_seen_at: d.last_seen_at ?? d.last_seen ?? null,
    organization_slug: d.organization_slug,
    created_at: d.created_at,
    updated_at: d.updated_at,
    meta: d.meta ?? null,
  }));

export const deviceSchema: z.ZodType<Device> = normalizeDevice;

export const devicesListSchema = z.array(normalizeDevice);

export interface DeviceAlert {
  alert_id: string;
  status: string;
  created_at: string;
}

export type AlertState = 'ACTIVE' | 'SNOOZED' | 'RESOLVED';

export interface Alert {
  alert_title: string;
  template_name: string;
  procedure: string | null;
  recurring: boolean;
  snooze_interval_minutes: number;
  device_name: string | null;
  device_mac: string;
  state: AlertState;
  resolution: string | null;
  triggered_at: string;
  first_event_at: string | null;
  resolved_at: string | null;
}

export interface AlertsResponse {
  alerts: Alert[];
  total: number;
}

export interface ListAlertsParams {
  device_id?: string;
  state?: AlertState;
  org_slug?: string;
}

export interface ResolveAlertsInput {
  device_mac: string;
  template_name: string;
  resolution?: string | null;
}

export interface AlertStateChangeInput {
  device_mac: string;
  template_name: string;
}

export enum HistoryField {
  LastFlashAt = 'last_flash_at',
  LastFlashStatus = 'last_flash_status',
  LastFlashError = 'last_flash_error',
  Connectivity = 'connectivity',
  GatewayMac = 'gateway_mac',
  StateOpen = 'state_open',
  Battery = 'battery',
  Uninstalled = 'uninstalled',
  Alert = 'alert',
  Uuid = 'uuid',
  Major = 'major',
  Minor = 'minor',
  Model = 'model',
  Product = 'product',
  RssiAtXm = 'rssi_at_xm',
  Screen = 'screen',
  Type = 'type',
  Ver = 'ver',
}

export enum HistoryFieldGroup {
  Flash = 'flash',
  Connectivity = 'connectivity',
  Gateway = 'gateway',
  DoorState = 'doorState',
  Battery = 'battery',
  Installation = 'installation',
  Alert = 'alert',
  ElabelMeta = 'elabelMeta',
  Other = 'other',
}

const HISTORY_FIELD_GROUPS: Record<HistoryField, HistoryFieldGroup> = {
  [HistoryField.LastFlashAt]: HistoryFieldGroup.Flash,
  [HistoryField.LastFlashStatus]: HistoryFieldGroup.Flash,
  [HistoryField.LastFlashError]: HistoryFieldGroup.Flash,
  [HistoryField.Connectivity]: HistoryFieldGroup.Connectivity,
  [HistoryField.GatewayMac]: HistoryFieldGroup.Gateway,
  [HistoryField.StateOpen]: HistoryFieldGroup.DoorState,
  [HistoryField.Battery]: HistoryFieldGroup.Battery,
  [HistoryField.Uninstalled]: HistoryFieldGroup.Installation,
  [HistoryField.Alert]: HistoryFieldGroup.Alert,
  [HistoryField.Uuid]: HistoryFieldGroup.ElabelMeta,
  [HistoryField.Major]: HistoryFieldGroup.ElabelMeta,
  [HistoryField.Minor]: HistoryFieldGroup.ElabelMeta,
  [HistoryField.Model]: HistoryFieldGroup.ElabelMeta,
  [HistoryField.Product]: HistoryFieldGroup.ElabelMeta,
  [HistoryField.RssiAtXm]: HistoryFieldGroup.ElabelMeta,
  [HistoryField.Screen]: HistoryFieldGroup.ElabelMeta,
  [HistoryField.Type]: HistoryFieldGroup.ElabelMeta,
  [HistoryField.Ver]: HistoryFieldGroup.ElabelMeta,
};

export const groupForHistoryField = (field: string): HistoryFieldGroup =>
  (HISTORY_FIELD_GROUPS as Partial<Record<string, HistoryFieldGroup>>)[field] ??
  HistoryFieldGroup.Other;

export interface DeviceHistoryEntry {
  field_name: string;
  old_value: string | null;
  new_value: string | null;
  old_display: string | null;
  new_display: string | null;
  changed_at: string;
}

export interface ListDeviceHistoryParams {
  mac_address: string;
  field_name?: string;
  from?: string;
  to?: string;
  limit?: number;
}

export type BinaryStateField = 'state_open' | 'uninstalled';

export interface BinaryStateTransition {
  changed_at: string;
  value: boolean;
}

export interface BinaryStateSeries {
  initial_value: boolean | null;
  transitions: BinaryStateTransition[];
}

export interface ListOpenStateParams {
  mac_address: string;
  from: string;
  to: string;
  field?: BinaryStateField;
}

export interface CreateDeviceInput {
  name: string;
  device_type: DeviceType;
  mac_address: string;
  organization_slug: string;
}

export interface UpdateDeviceInput {
  name?: string | null;
  device_type?: DeviceType;
  status?: DeviceStatus;
  organization_slug?: string | null;
}

export interface ListDevicesParams {
  device_type?: DeviceType;
  status?: DeviceStatus;
  organization_slug?: string;
}

export interface DevicePosition {
  mac_address: string;
  pos_x: number;
  pos_y: number;
}

export interface SetDevicePositionInput {
  mac_address: string;
  pos_x: number;
  pos_y: number;
}

export interface ClearDevicePositionInput {
  mac_address: string;
}

export const devicePositionSchema: z.ZodType<DevicePosition> = z.object({
  mac_address: z.string(),
  pos_x: z.number(),
  pos_y: z.number(),
});

export const devicePositionsListSchema = z.array(devicePositionSchema);

export interface DeviceGatewayAssignment {
  device_mac: string;
  gateway_mac: string;
  updated_at: string;
}

export interface ListDeviceGatewayAssignmentsParams {
  gateway_mac?: string;
}

export const deviceGatewayAssignmentSchema: z.ZodType<DeviceGatewayAssignment> = z.object({
  device_mac: z.string(),
  gateway_mac: z.string(),
  updated_at: z.string(),
});

export const deviceGatewayAssignmentsListSchema = z.array(deviceGatewayAssignmentSchema);

export interface DeviceRssiReading {
  device_mac: string;
  gateway_mac: string;
  rssi: number;
  updated_at: string;
}

export interface ListDeviceRssiParams {
  device_mac?: string;
  gateway_mac?: string;
}

export const deviceRssiReadingSchema: z.ZodType<DeviceRssiReading> = z.object({
  device_mac: z.string(),
  gateway_mac: z.string(),
  rssi: z.number(),
  updated_at: z.string(),
});

export const deviceRssiReadingsListSchema = z.array(deviceRssiReadingSchema);
